// =============================================================================
// Night Operations Store — global low-light mode management
// =============================================================================
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type NightPreset = 'dark-office' | 'field-inspection' | 'cinema' | 'blueprint';
export type TextScale  = 'small' | 'medium' | 'large' | 'xl';

const TEXT_SCALE_MAP: Record<TextScale, number> = {
  small:  0.95,
  medium: 1.0,
  large:  1.15,
  xl:     1.25,
};

export const NIGHT_PRESETS: Record<NightPreset, {
  label:       string;
  description: string;
  icon:        string;
  brightness:  number;
  contrast:    number;
  textScale:   TextScale;
  // CSS token overrides (RGB triplets)
  tokens: {
    bg:      string;
    panel:   string;
    border:  string;
    fg:      string;
    muted:   string;
    accent:  string;
  };
}> = {
  'dark-office': {
    label:       'Dark Office',
    description: 'Balanced professional low-light mode',
    icon:        '🏢',
    brightness:  88,
    contrast:    95,
    textScale:   'large',
    tokens: {
      bg:     '8  10  18',
      panel:  '11  15  24',
      border: '28  42  68',
      fg:     '214 224 240',
      muted:  '100 120 155',
      accent: '20 184 166',
    },
  },
  'field-inspection': {
    label:       'Field Inspection',
    description: 'High contrast for outdoor/construction sites',
    icon:        '🔦',
    brightness:  94,
    contrast:    110,
    textScale:   'xl',
    tokens: {
      bg:     '5   8  14',
      panel:  '8  12  20',
      border: '45  80 110',
      fg:     '240 248 255',
      muted:  '140 180 210',
      accent: '56 220 160',
    },
  },
  'cinema': {
    label:       'Cinema Mode',
    description: 'Near-dark for video review and presentations',
    icon:        '🎬',
    brightness:  55,
    contrast:    90,
    textScale:   'large',
    tokens: {
      bg:     '4   5   8',
      panel:  '6   8  12',
      border: '18  24  36',
      fg:     '190 200 218',
      muted:  '70  82 100',
      accent: '18 160 148',
    },
  },
  'blueprint': {
    label:       'Blueprint Mode',
    description: 'Technical drafting — dark background, blue/white lines',
    icon:        '📐',
    brightness:  82,
    contrast:    100,
    textScale:   'medium',
    tokens: {
      bg:     '2   8  22',
      panel:  '4  12  30',
      border: '30  60 120',
      fg:     '200 220 255',
      muted:  '80 110 180',
      accent: '60 130 246',
    },
  },
};

interface NightOpsState {
  active:        boolean;
  preset:        NightPreset;
  brightness:    number;   // 0–100
  contrast:      number;   // 0–100
  textScale:     TextScale;
  focusBeam:     boolean;
  reduceGlow:    boolean;
  reduceMotion:  boolean;

  // Actions
  activate:      () => void;
  deactivate:    () => void;
  toggle:        () => void;
  setPreset:     (p: NightPreset) => void;
  setBrightness: (v: number) => void;
  setContrast:   (v: number) => void;
  setTextScale:  (s: TextScale) => void;
  toggleFocusBeam: () => void;
  setReduceGlow:   (v: boolean) => void;
  setReduceMotion: (v: boolean) => void;
}

export const useNightOpsStore = create<NightOpsState>()(
  persist(
    (set, get) => ({
      active:       false,
      preset:       'dark-office',
      brightness:   88,
      contrast:     95,
      textScale:    'large',
      focusBeam:    false,
      reduceGlow:   false,
      reduceMotion: false,

      activate:      () => set({ active: true }),
      deactivate:    () => set({ active: false }),
      toggle:        () => set((s) => ({ active: !s.active })),
      setPreset:     (p) => {
        const preset = NIGHT_PRESETS[p];
        set({
          preset:     p,
          brightness: preset.brightness,
          contrast:   preset.contrast,
          textScale:  preset.textScale,
        });
      },
      setBrightness: (v) => set({ brightness: v }),
      setContrast:   (v) => set({ contrast: v }),
      setTextScale:  (s) => set({ textScale: s }),
      toggleFocusBeam: () => set((s) => ({ focusBeam: !s.focusBeam })),
      setReduceGlow:   (v) => set({ reduceGlow: v }),
      setReduceMotion: (v) => set({ reduceMotion: v }),
    }),
    { name: 'r2.night-ops' },
  ),
);

// Helper to get CSS font-scale multiplier for current text scale
export function getTextScaleMultiplier(s: TextScale): number {
  return TEXT_SCALE_MAP[s];
}
